import { createElement, type ReactElement } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getPlatformIcon, getPlatformLabel } from './platform-utils';

interface PlatformBadgeProps {
  /** The platform identifier (cursor, claude, copilot, vscode) */
  platform: string;
  /** Optional tooltip text (e.g., last active timestamp) */
  tooltipText?: string;
  /** Whether to show the tooltip. Defaults to true if tooltipText is provided */
  showTooltip?: boolean;
  /** Optional custom label override */
  label?: string;
  /** Optional extra classes merged into the badge (e.g. mismatch highlight). */
  className?: string;
}

/**
 * Badge component for displaying an IDE platform with its icon
 *
 * Features:
 * - Shows platform icon and label
 * - Optional tooltip with custom text
 * - Supports all known platforms: Cursor, Claude Code, Copilot, VS Code
 * - Forward-compatible with unknown platforms (shows generic icon)
 */
export function PlatformBadge({
  platform,
  tooltipText,
  showTooltip = true,
  label,
  className,
}: PlatformBadgeProps): ReactElement {
  const displayLabel = label ?? getPlatformLabel(platform);

  const badge = (
    <Badge variant="outline" className={cn('gap-1', className)}>
      {createElement(getPlatformIcon(platform), {
        className: 'h-3 w-3',
      })}
      {displayLabel}
    </Badge>
  );

  if (!showTooltip || !tooltipText) {
    return badge;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>
        <span>{tooltipText}</span>
      </TooltipContent>
    </Tooltip>
  );
}
